import React, { useMemo, Suspense, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import Editor from "../../../../common/RichTextEditor/Editor/Editor";
import { SharedHistoryContext } from "../../../../common/RichTextEditor/LexicalEditor/context/SharedHistoryContext";
import { TableContext } from "../../../../common/RichTextEditor/LexicalEditor/plugins/TablePlugin";
import { ToolbarContext } from "../../../../common/RichTextEditor/LexicalEditor/context/ToolbarContext";
import PlaygroundNodes from "../../../../common/RichTextEditor/LexicalEditor/nodes/PlaygroundNodes";
import PlaygroundEditorTheme from "../../../../common/RichTextEditor/LexicalEditor/themes/PlaygroundEditorTheme";
import { buildImportMap } from "../../../../util/lexical";
import RenderHtml from "../../../../common/AppSpecificConpnents/PartsTable/RenderHtml";
import "../../../../common/RichTextEditor/Editor/index.css";
import { pingTask } from "../../../../util/apiService";
import {
  NODE_SELECTION_BLUE,
  isDescendantLockedPreview,
  isAuditPreviewFocusedLeafCheckbox,
  getDescendantLockedCheckboxStyle,
  getHaloWrapperStyle,
  getAuditPreviewReadonlyHaloCheckboxStyle,
  auditPreviewSectionDescendantWrapStyle,
} from "./nodeSelectionCheckboxStyles";

import "../../../../common/RichTextEditor/LexicalEditor/themes/PlaygroundEditorTheme.css";

const RichTextNode = ({
  node,
  nodeNumber,
  parentContext,
  nodeType,
  onNavigate,
  checkboxState,
  disabled = false,
  showCheckbox = true,
  showHeading = null,
  checkboxLabelColor = null,
  isRevisionLocked = false,
  isLock = false,
}) => {
  const authUser = useSelector((state) => state.auth.user);
  const nodeHeading = `Rich Text ${node.is_deleted ? "(Deleted)" : nodeNumber}`;
  const breadcrumbStepNumber =
    parentContext?.globalStepNumber != null
      ? parentContext.globalStepNumber
      : typeof parentContext?.stepIndex === "number"
        ? parentContext.stepIndex + 1
        : "";
  const rawRichTextContent = node.detail?.rich_text_content || "";
  
  // Get revisionId and authUser for ping API
  const location = useLocation();
  const params = useParams();
  
  // Extract revisionId from params (could be revisionId or revision_id depending on route)
  const revisionId = params.revisionId || params.revision_id;
  
  // Check if we're on a view-revision route
  const isViewRevisionRoute = location.pathname.includes('/view-revision') || 
                               location.pathname.includes('/view-section') ||
                               location.pathname.includes('/view-step') ||
                               location.pathname.includes('/view-substep') ||
                               location.pathname.includes('/view-childnode');
  
  // Start ping interval if on view-revision route
  useEffect(() => {
    if (!isViewRevisionRoute || !revisionId || !authUser) return;
    
    const interval = setInterval(async () => {
      try {
        const pingData = {
          task_id: revisionId,
          user_id: authUser.id
        };
        await pingTask(pingData);
      } catch (error) {
        console.error('Ping failed in RichTextNode:', error);
      }
    }, 60000); // 60 seconds = 1 minute
    
    return () => {
      clearInterval(interval);
    };
  }, [isViewRevisionRoute, revisionId, authUser]);

  // Process content the same way RichTextForm does
  // This ensures consistent rendering between editor and display
  const richTextContent = useMemo(() => {
    if (!rawRichTextContent) return "";

    // Process content similar to setInitialHtmlContent in RichTextForm
    // Only remove editor-shell wrapper if present (same logic as RichTextForm)
    let processedContent = rawRichTextContent;
    if (rawRichTextContent && rawRichTextContent.includes('class="editor-shell"')) {
      // Remove the editor-shell wrapper if present (same regex as RichTextForm)
      processedContent = rawRichTextContent.replace(/^<div class="editor-shell">(.*)<\/div>\s*$/s, '$1');
    }

    return processedContent;
  }, [rawRichTextContent]);

  // Lexical editor configuration for read-only display
  const initialConfig = useMemo(
    () => ({
      editorState: null, // Content will be set via initialContent prop in Editor
      html: { import: buildImportMap() },
      namespace: "Playground",
      nodes: [...PlaygroundNodes],
      onError: (error) => {
        console.error("Error occurred in RichTextNode Lexical editor:", error);
      },
      theme: PlaygroundEditorTheme,
      readOnly: true,
      editable: false,
    }),
    []
  );

  // Determine checkbox state for this node
  const isCurrent =
    checkboxState?.currentNodeId === node.tree_node_id &&
    (checkboxState?.currentNodeType === "rich-text" ||
      checkboxState?.currentNodeType === nodeType);
  const isSourceRichText =
    checkboxState?.sourceNodeId === node.tree_node_id ||
    checkboxState?.sourceNodeId ===
    (node.detail?.rich_text_node_id || node.tree_node_id);
  const isChecked =
    isCurrent || checkboxState?.allChildrenChecked || isSourceRichText;
  const isDisabled =
    disabled || (checkboxState?.allChildrenDisabled && !isCurrent) || (isRevisionLocked && isRevisionLocked.lock && isRevisionLocked.lock.user_id !== authUser?.id);
  const isDescendantLocked = isDescendantLockedPreview(
    checkboxState,
    isCurrent,
    isChecked,
    isSourceRichText,
  );
  const isAuditPreviewFocusedLeaf = isAuditPreviewFocusedLeafCheckbox(
    checkboxState,
    isCurrent,
    isChecked,
    isSourceRichText,
  );
  const isPreviewLockedLeafStyle = isDescendantLocked || isAuditPreviewFocusedLeaf;

  return (
    <div
      key={node.tree_node_id}
      data-export-block="rich-text"
      style={{ marginTop: showCheckbox ? "20px" : "30px", clear: "both" }}
    >
      {/* Show heading - use showHeading if provided, otherwise fall back to showCheckbox */}
      {(showHeading !== null ? showHeading : showCheckbox) && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
            }}
          >
            {showCheckbox && (
              <div
                style={{
                  position: "relative",
                  display: "inline-block",
                  ...auditPreviewSectionDescendantWrapStyle(checkboxState),
                }}
              >
                {isDescendantLocked ? (
                  <input
                    type="checkbox"
                    checked={isChecked}
                    disabled
                    readOnly
                    tabIndex={-1}
                    aria-readonly="true"
                    onChange={() => {}}
                    style={getDescendantLockedCheckboxStyle()}
                  />
                ) : isAuditPreviewFocusedLeaf ? (
                  <div style={getHaloWrapperStyle()}>
                    <input
                      type="checkbox"
                      checked={isChecked}
                      tabIndex={-1}
                      aria-readonly="true"
                      onChange={() => {}}
                      style={getAuditPreviewReadonlyHaloCheckboxStyle()}
                    />
                  </div>
                ) : (
                <input
                  type="checkbox"
                  checked={isChecked}
                  disabled={isDisabled}
                  onChange={() => {
                    // Prevent action for source rich text being moved
                    if (isSourceRichText) {
                      return;
                    }
                    if (!isDisabled && onNavigate) {
                      onNavigate(nodeType || "rich-text", node, { ...parentContext, nodeNumber });
                    }
                  }}
                  style={{
                    cursor: isDisabled ? "not-allowed" : (isSourceRichText ? "not-allowed" : "pointer"),
                    accentColor: isSourceRichText
                      ? "rgb(68, 68, 68)"
                      : isChecked
                        ? NODE_SELECTION_BLUE
                        : undefined,
                    backgroundColor: isSourceRichText ? "rgb(68, 68, 68)" : undefined,
                  }}
                />
                )}
                {/* Red cross icon overlay - shown when disabled and hovering */}
                {isDisabled && !isPreviewLockedLeafStyle && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      opacity: 0,
                      transition: "opacity 0.2s ease-in-out",
                      pointerEvents: "none",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.opacity = "1";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.opacity = "0";
                    }}
                    className="checkbox-hover-overlay"
                  >
                    <FontAwesomeIcon
                      icon={faTimes}
                      size="sm"
                      color="red"
                      style={{
                        filter: "drop-shadow(0 0 1px rgba(255,255,255,0.8))",
                      }}
                    />
                  </div>
                )}
              </div>
            )}
            <h2
              onClick={() => {
                // Prevent action for source rich text being moved
                if (isSourceRichText) {
                  return;
                }
                if (isPreviewLockedLeafStyle) return;
                if (!isDisabled && showCheckbox && onNavigate) {
                  onNavigate(nodeType || "rich-text", node, { ...parentContext, nodeNumber });
                }
              }}
              style={{
                fontSize: "20px",
                margin: 0,
                fontWeight: isPreviewLockedLeafStyle ? 700 : undefined,
                cursor: isSourceRichText
                  ? "not-allowed"
                  : isPreviewLockedLeafStyle
                    ? "default"
                  : !isDisabled && showCheckbox
                    ? "pointer"
                    : "default",
                color: isPreviewLockedLeafStyle
                  ? "#111"
                  : showCheckbox
                    ? checkboxLabelColor || undefined
                    : undefined,
                userSelect: "none",
              }}
            >
              {nodeHeading}
            </h2>
          </div>

          <div
            style={{
              color: "#888",
              fontSize: "14px",
              fontWeight: "normal",
            }}
          >
            {!node.is_deleted &&
              (parentContext?.auditSubstepBreadcrumbFull ? (
                <>{parentContext.auditSubstepBreadcrumbFull}</>
              ) : (
                <>
                  Section {parentContext.sectionIndex + 1} &gt; Step{" "}
                  {breadcrumbStepNumber}
                  {parentContext.substepLetter && (
                    <> &gt; Substep {breadcrumbStepNumber}{parentContext.substepLetter}</>
                  )}
                </>
              ))}
          </div>
        </div>
      )}

      {/* Short Description rendered via Lexical read-only viewer for exact UI parity */}
      {(() => {
        const rawShort = node.detail?.short_description || "";
        // Convert plain text with quotes to HTML format for proper rendering
        const contentHtml =
          typeof rawShort === "string" && rawShort.includes('class="editor-shell"')
            ? rawShort.replace(/^<div class="editor-shell">(.*)<\/div>\s*$/s, "$1")
            : rawShort && rawShort.trim() !== ""
              ? rawShort.includes('<') && rawShort.includes('>')
                ? rawShort // Already HTML, use as-is
                : `<p>${rawShort.replace(/"/g, '&quot;')}</p>` // Plain text, wrap in p tags
              : rawShort;
        return contentHtml ? (
          <div style={{ marginTop: "5px" }}>
            <div className="rich-text-content-display">
              <Suspense fallback={<div>Loading...</div>}>
                <LexicalComposer
                  initialConfig={{
                    editorState: null,
                    html: { import: buildImportMap() },
                    namespace: "ShortDescriptionViewer",
                    nodes: [...PlaygroundNodes],
                    onError: (error) => {
                      console.error("Error in Short Description viewer:", error);
                    },
                    theme: PlaygroundEditorTheme,
                    readOnly: true,
                    editable: false,
                  }}
                >
                  <SharedHistoryContext>
                    <TableContext>
                      <ToolbarContext>
                        <div className="editor-shell">
                          <Editor
                            handleEditorChange={() => {}}
                            initialContent={contentHtml}
                          />
                        </div>
                      </ToolbarContext>
                    </TableContext>
                  </SharedHistoryContext>
                </LexicalComposer>
              </Suspense>
            </div>
          </div>
        ) : null;
      })()}
      <div
        style={{
          marginTop: "5px",
        }}
      >
        {node.detail?.category_info?.category_name}
      </div>
      <div
        style={{
          fontSize: "14px",
          marginTop: "5px",
        }}
      >
        {richTextContent ? (
          <>
            <div className="rich-text-content-display">
              <Suspense fallback={<div>Loading...</div>}>
                <LexicalComposer initialConfig={initialConfig}>
                  <SharedHistoryContext>
                    <TableContext>
                      <ToolbarContext>
                        <div className="editor-shell">
                          <Editor
                            handleEditorChange={() => { }}
                            initialContent={richTextContent}
                          />
                        </div>
                      </ToolbarContext>
                    </TableContext>
                  </SharedHistoryContext>
                </LexicalComposer>
              </Suspense>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default RichTextNode;

